import { useState, useEffect, useCallback } from 'react';

// Two separate signals — industry standard pattern:
//   dismissed → user has seen and closed the tour (skip OR finish); stops auto-open.
//   completed → user finished all steps; removes the header badge nudge.
const DISMISSED_KEY = 'vl_tour_dismissed';
const COMPLETED_KEY = 'vl_tour_completed';

export function useTour() {
  const [isTourOpen, setIsTourOpen] = useState(false);
  // Initialise from storage so the badge state is correct on first render.
  // Guarded with try/catch: localStorage.getItem can throw in private-browsing
  // or hardened-browser environments; fall back to false so the hook never
  // crashes the app on mount.
  const [tourCompleted, setTourCompleted] = useState(() => {
    try { return !!localStorage.getItem(COMPLETED_KEY); } catch { return false; }
  });

  // Auto-open exactly once — on the very first visit.
  // Any prior dismissal (skip or finish) prevents further auto-opens.
  // A short delay lets the DOM fully settle so spotlight BoundingClientRects
  // are accurate on the first targeted step.
  useEffect(() => {
    try {
      const alreadySeen =
        localStorage.getItem(DISMISSED_KEY) || localStorage.getItem(COMPLETED_KEY);
      if (alreadySeen) return;
    } catch {
      // Storage unavailable — skip auto-open rather than crash.
      return;
    }
    const timer = setTimeout(() => setIsTourOpen(true), 400);
    return () => clearTimeout(timer);
  }, []);

  /** Open the tour manually (header button). Always works regardless of storage. */
  const openTour = useCallback(() => setIsTourOpen(true), []);

  /**
   * User dismissed early (×, Skip, or backdrop click).
   * Marks dismissed so the tour never auto-opens again.
   * Badge remains as a gentle nudge to come back when ready.
   */
  const skipTour = useCallback(() => {
    try { localStorage.setItem(DISMISSED_KEY, 'true'); } catch { /* ignore */ }
    setIsTourOpen(false);
  }, []);

  /**
   * User completed all steps (clicked Finish).
   * Marks both dismissed and completed — badge disappears.
   */
  const completeTour = useCallback(() => {
    try {
      localStorage.setItem(DISMISSED_KEY, 'true');
      localStorage.setItem(COMPLETED_KEY, 'true');
    } catch { /* ignore — badge will reset on next load but tour closes */ }
    setTourCompleted(true);
    setIsTourOpen(false);
  }, []);

  return { isTourOpen, tourCompleted, openTour, skipTour, completeTour };
}
